import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fetchMyTeams } from "../../services/teamsService";
import { 
  Users, 
  Plus, 
  ChevronRight, 
  Loader2,
  Target,
  Crown
} from "lucide-react";
import DashboardNav from "../../components/DashboardNav";
import CreateTeamModal from "../../components/CreateTeamModal";

const TeamsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await fetchMyTeams();
        setTeams(data);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadTeams();
    }
  }, [isAuthenticated, navigate]);

  const handleTeamCreated = (newTeam) => {
    setTeams([newTeam, ...teams]);
    setShowCreateModal(false);
    // Navigate to the new team
    navigate(`/dashboard/teams/${newTeam.team_id}`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <DashboardNav />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">My Teams</h1>
            <p className="text-zinc-400 mt-1">
              {teams.length === 0 
                ? "Create a team to track achievements together"
                : `${teams.length} team${teams.length !== 1 ? 's' : ''}`
              }
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-team-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Team
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Teams List */}
        {teams.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-zinc-500" />
            </div>
            <h2 className="text-lg font-medium text-white mb-2">No teams yet</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Create a team to start tracking weekly achievements with your colleagues. 
              Perfect for small, fast-moving teams.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Your First Team
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {teams.map((team) => (
              <button
                key={team.team_id}
                onClick={() => navigate(`/dashboard/teams/${team.team_id}`)}
                data-testid={`team-card-${team.team_id}`}
                className="w-full p-4 bg-zinc-900 hover:bg-zinc-800 rounded-xl border border-zinc-800 hover:border-zinc-700 transition-all text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-white">{team.name}</h3>
                        {team.my_role === 'admin' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-amber-400 text-xs">
                            <Crown className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-zinc-400">
                          {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                        </span>
                        {team.quarterly_goal && (
                          <span className="flex items-center gap-1 text-sm text-zinc-500">
                            <Target className="w-3 h-3" />
                            Goal set
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>
                {team.description && (
                  <p className="mt-3 text-sm text-zinc-500 line-clamp-2 pl-16">
                    {team.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Create Team Modal */}
      {showCreateModal && (
        <CreateTeamModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleTeamCreated}
        />
      )}
    </div>
  );
};

export default TeamsPage;
